import { useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { FieldSetBox } from "../fieldSetBox";
import { ComparablesTable } from "./comparablesTable";
import { ReportImages } from "../../stores/internalValuationReviewStore";

const useStyles = makeStyles(() => ({
  page: {
    fontFamily: "Roboto, Arial, sans-serif",
    border: "1px solid lightgray",
    borderRadius: "5px",
    width: "1018px",
    height: "1440px",
    padding: "64px 40px 64px 40px",
  },
  //the page of the comments only grows if the comments turn out taller than a page, instead of cutting them off
  growingPage: {
    height: "auto",
    minHeight: "1440px",
  },
}));

//true when the content ends inside the page (without its bottom padding)
const contentFits = (page: HTMLElement, content: HTMLElement) => {
  const pageTop = page.getBoundingClientRect().top;
  const paddingBottom = parseFloat(getComputedStyle(page).paddingBottom) || 0;
  const contentBottom = pageTop + page.clientTop + page.clientHeight - paddingBottom;
  return content.getBoundingClientRect().bottom <= contentBottom + 0.5;
};

interface ComparablesPageProps {
  //the subject property first, then the comps of this page
  compForms: any[];
  startIndex: number;
  subjectPropertyColumnBg: string;
  reportImages?: ReportImages | null;
}

//A page of the comparables table. The MLS and analyst comments are shown whole: when they do not fit in the page
//(it has a fixed size) they go to the next page, under the same subject and comps headers.
export const ComparablesPage = ({ compForms, startIndex, subjectPropertyColumnBg, reportImages }: ComparablesPageProps) => {
  const classes = useStyles();
  const pageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const [commentsOnNextPage, setCommentsOnNextPage] = useState(false);

  //the page is first rendered with everything, the real fonts and widths decide if it fits (before it is painted)
  useLayoutEffect(() => {
    if (pageRef.current && contentRef.current && !contentFits(pageRef.current, contentRef.current)) {
      setCommentsOnNextPage(true);
    }
  }, []);

  return (
    <>
      <div ref={pageRef} className={classes.page}>
        <main ref={contentRef}>
          <FieldSetBox title="Comparable">
            <ComparablesTable
              compForms={compForms}
              startIndex={startIndex}
              subjectPropertyColumnBg={subjectPropertyColumnBg}
              reportImages={reportImages}
              rowSet={commentsOnNextPage ? "withoutComments" : "all"}
            />
          </FieldSetBox>
        </main>
      </div>
      {commentsOnNextPage && (
        <div className={`${classes.page} ${classes.growingPage}`}>
          <main>
            <FieldSetBox title="Comparable (continued)">
              <ComparablesTable
                compForms={compForms}
                startIndex={startIndex}
                subjectPropertyColumnBg={subjectPropertyColumnBg}
                reportImages={reportImages}
                rowSet="commentsOnly"
              />
            </FieldSetBox>
          </main>
        </div>
      )}
    </>
  );
};
